import React, { FC, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import sortBy from 'lodash/sortBy';

import { Body, Row, Section } from 'src/presentation/components/forms/Layout';
import { Button } from 'src/presentation/components/agent_desktop_components';
import generateId from 'src/lib/generateId';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { useForm, FormEditorProps } from 'src/presentation/components/forms/Form';
import { H1 } from '@ad-scripts/presentation/components/common/headers';
import Input from 'src/presentation/components/forms/Input';
import keywordGroupsResource, { KeywordGroup } from 'src/actions/keywordGroups/keywordGroupsResource';
import Textarea from 'src/presentation/components/forms/Textarea';
import availableLanguagesResource from 'src/actions/answers/availableLanguagesResource';
import useResource from 'src/presentation/hooks/useResource';
import DropdownAdmin from 'src/presentation/components/forms/Dropdown';
import { default as Languages } from '@ad-scripts/domain/models/languages/answer_languages';
import answerLanguagesResource from 'src/actions/answers/answerLanguagesResource';
import useDocumentResource from 'src/presentation/hooks/useDocumentResource';
import analytics from '@ad-scripts/lib/analytics';

const dataAidPrefix = 'admin-keyword-group-';
const nameDescription =
  'This name helps identify the keywords of the group. The Group Name is not included in the keyword group.';
const keywordsDescription =
  'When searching for answers with a word that exists in a keyword group, all keywords in this group are also included as part of the search. Separate each keyword with a comma.';
const languagesDescription =
  'When searching for answers in a specific language, only keyword groups specified for that language are applied to the search.';

const KeywordGroupEditor: FC<FormEditorProps> = ({ errors, item, root }) => {
  const add = keywordGroupsResource.effects.add;
  const update = keywordGroupsResource.effects.update;
  const validate = keywordGroupsResource.effects.validate;

  const formContext = useForm({
    add,
    defaultItem: { label: '', phrases: '', languages: [] },
    errors,
    item: item
      ? {
          ...item,
          phrases: item.phrases ? item.phrases.join(', ') : '',
          languages: item.languages ? item.languages : [],
        }
      : null,
    resetErrors: keywordGroupsResource.actions.resetErrors,
    root,
    update,
  });
  const languages = useAvailableAnswerLanguages();

  const { onClose, isNew } = formContext;

  const onSave = useOnSave({ ...formContext, add, update, validate });

  return (
    <Form context={formContext}>
      <H1>{isNew ? 'Create' : 'Edit'} Keyword Group</H1>

      <Body data-aid={`${dataAidPrefix}editor`}>
        <Section>
          <Row>
            <Input
              data-aid={`${dataAidPrefix}editor-label-input`}
              attr="label"
              autoFocus
              label="Group Name"
              placeholder="e.g. Shoes"
              description={nameDescription}
            />
          </Row>
          <Row>
            <Textarea
              data-aid={`${dataAidPrefix}editor-phrases-input`}
              attr="phrases"
              label="Keywords"
              placeholder="e.g. shoes, sneakers, heels, kicks"
              description={keywordsDescription}
            />
          </Row>
          <Row>
            <DropdownAdmin
              autoFocus
              data-aid={`${dataAidPrefix}editor-name-input`}
              description={languagesDescription}
              label="Language"
              attr="languages"
              isMultiSelect
              maxRows={5}
              multiSelectLabel="languages"
              options={languages}
              placeholder="Select Languages"
            />
          </Row>
        </Section>
      </Body>

      <Footer>
        <Button data-aid={`${dataAidPrefix}editor-cancel`} type={Button.Types.TEXT} onClick={onClose}>
          Cancel
        </Button>
        <Button data-aid={`${dataAidPrefix}editor-save`} onClick={onSave}>
          Save
        </Button>
      </Footer>
    </Form>
  );
};

const useAvailableAnswerLanguages = () => {
  const { loadingState: usedLangLoadingState, value: usedLangs } = useDocumentResource(answerLanguagesResource);
  const { loadingState: availableLangLoadingState } = useResource(availableLanguagesResource);

  const isLoaded = usedLangLoadingState === 'succeeded' && availableLangLoadingState === 'succeeded';

  return useMemo(
    () => {
      const usedLanguages = new Set(usedLangs);
      const languages: { label: string; value: string }[] = [];
      usedLanguages.forEach(lang => {
        const langId = lang as string;
        languages.push({ label: Languages[langId], value: langId });
      });
      return sortBy(languages, ['label']);
    },
    // don't update language list after languages have loaded
    // eslint-disable-next-line
    [isLoaded]
  );
};

function useOnSave({ add, isNew, item, update, validate, workingState }) {
  const dispatch = useDispatch();
  return useCallback(() => {
    const updatedAt = new Date().toISOString();
    // split out individual keywords and ignore empty
    const phraseArrFromString = workingState.phrases.split(/[;|,] |[\n\r]+/g).filter(p => p);
    const languageArray = workingState.languages;
    const draft = { ...workingState, phrases: phraseArrFromString, languages: languageArray, updatedAt };
    if (!dispatch(validate(draft, KeywordGroup))) return;

    const analyticsMeta = {
      countKeywords: draft.phrases.length,
      countLanguages: draft.languages.length,
    };
    if (isNew) {
      analytics.track('Admin - Keyword Group Added', analyticsMeta);
      dispatch(add({}, { id: generateId(), ...draft }));
    } else {
      analytics.track('Admin - Keyword Group Updated', analyticsMeta);
      dispatch(update({ id: item.id }, draft));
    }
  }, [add, dispatch, isNew, item, update, validate, workingState]);
}

export default KeywordGroupEditor;
